import { keepPreviousData, useQuery, QueryFunctionContext } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';

// Query Keys
export const BILLING_PERFORMANCE_KPI_QUERY_KEY = 'billing-performance-kpi';
export const BILLING_PERFORMANCE_SUMMARIES_QUERY_KEY = 'billing-performance-summaries';
export const BILLING_PERFORMANCE_ORDER_DETAILS_QUERY_KEY = 'billing-performance-order-details';
export const BILLING_PERFORMANCE_ROUTE_DETAILS_QUERY_KEY = 'billing-performance-route-details';

// Types
export type KpiValue = {
  value: number;
  change_rate: number;
};

export type BillingPerformanceKpiResponse = {
  total_cases: KpiValue;
  billing_amount: KpiValue;
  payment_amount: KpiValue;
  gross_profit: KpiValue;
  gross_profit_rate: KpiValue;
  total_weight: KpiValue;
  total_volume: KpiValue;
  total_distance: KpiValue;
};

export type SummaryDataResponse = {
  id: number;
  date: string | null;
  name: string;
  entity_id: number | null;
  billing: number;
  payment: number;
  gross_profit: number;
  gross_profit_rate: number;
  cases: number;
  quantity: number;
  weight: number;
  volume: number;
  distance: number;
};

export type OrderDetailDataResponse = {
  id: number;
  billing_tariff: string;
  payment_tariff: string;
  carrier: string;
  vehicle: string;
  driver: string;
  pickup_date: string;
  dropoff_date: string;
  pickup_name: string;
  delivery_name: string;
  pickup_prefecture: string;
  delivery_prefecture: string;
  quantity: number;
  weight: number;
  volume: number;
  billing: number;
  payment: number;
};

export type RouteDetailDataResponse = {
  id: number;
  billing_tariff: string;
  payment_tariff: string;
  carrier: string;
  vehicle: string;
  driver: string;
  pickup_date: string;
  dropoff_date: string;
  distance: number;
  time: number;
  quantity: number;
  weight: number;
  volume: number;
  billing: number;
  payment: number;
};

export type PaginationMeta = {
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
};

export type SummariesResponse = {
  data: SummaryDataResponse[];
  meta: PaginationMeta;
};

export type OrderDetailsResponse = {
  data: OrderDetailDataResponse[];
  meta: PaginationMeta;
};

export type RouteDetailsResponse = {
  data: RouteDetailDataResponse[];
  meta: PaginationMeta;
};

// Parameter Types
export type KpiParams = {
  startDate: Date | null;
  endDate: Date | null;
  locationId?: number;
  periodMode?: 'daily' | 'monthly';
};

export type SummariesParams = {
  startDate: Date | null;
  endDate: Date | null;
  groupBy: 'shipper' | 'carrier' | 'truck' | 'driver';
  locationId?: number;
  keyword?: string;
  page?: number;
  perPage?: number;
  sort?: string;
  periodMode?: 'daily' | 'monthly';
};

export type DetailParams = {
  startDate: Date | null;
  endDate: Date | null;
  groupBy?: 'shipper' | 'carrier' | 'truck' | 'driver';
  entityId?: number;
  entityName?: string;
  locationId?: number;
  page?: number;
  perPage?: number;
  sort?: string;
  periodMode?: 'daily' | 'monthly';
  summaryDate?: string;
};

// Helper function to format date
const formatDate = (date: Date | null): string | undefined => {
  if (!date) return undefined;
  return format(date, 'yyyy-MM-dd');
};

// Helper to add common params to URLSearchParams
const addPeriodModeParam = (searchParams: URLSearchParams, periodMode?: string) => {
  if (periodMode) searchParams.set('period_mode', periodMode);
};

// KPI Summary Query
type KpiQueryKey = [typeof BILLING_PERFORMANCE_KPI_QUERY_KEY, KpiParams];

const fetchKpiSummary = async ({ queryKey }: QueryFunctionContext<KpiQueryKey>) => {
  const [, params] = queryKey;
  const searchParams = new URLSearchParams();

  const startDate = formatDate(params.startDate);
  const endDate = formatDate(params.endDate);

  if (startDate) searchParams.set('start_date', startDate);
  if (endDate) searchParams.set('end_date', endDate);
  if (params.locationId) searchParams.set('location_id', String(params.locationId));
  addPeriodModeParam(searchParams, params.periodMode);

  const { data } = await axios.get<BillingPerformanceKpiResponse>(
    `/api/v5/billing_performances/kpi_summary?${searchParams.toString()}`
  );
  return data;
};

export const useQueryBillingPerformanceKpi = (params: KpiParams) => (
  useQuery<BillingPerformanceKpiResponse, Error>({
    queryKey: [BILLING_PERFORMANCE_KPI_QUERY_KEY, params],
    queryFn: fetchKpiSummary,
    enabled: !!params.startDate && !!params.endDate,
    refetchOnWindowFocus: false,
  })
);

// Summaries Query
type SummariesQueryKey = [typeof BILLING_PERFORMANCE_SUMMARIES_QUERY_KEY, SummariesParams];

const fetchSummaries = async ({ queryKey }: QueryFunctionContext<SummariesQueryKey>) => {
  const [, params] = queryKey;
  const searchParams = new URLSearchParams();

  const startDate = formatDate(params.startDate);
  const endDate = formatDate(params.endDate);

  if (startDate) searchParams.set('start_date', startDate);
  if (endDate) searchParams.set('end_date', endDate);
  searchParams.set('group_by', params.groupBy);
  if (params.locationId) searchParams.set('location_id', String(params.locationId));
  if (params.keyword) searchParams.set('keyword', params.keyword);
  if (params.page) searchParams.set('page', String(params.page));
  if (params.perPage) searchParams.set('per_page', String(params.perPage));
  if (params.sort) searchParams.set('sort', params.sort);
  addPeriodModeParam(searchParams, params.periodMode);

  const { data } = await axios.get<SummariesResponse>(
    `/api/v5/billing_performances/summaries?${searchParams.toString()}`
  );
  return data;
};

export const useQueryBillingPerformanceSummaries = (params: SummariesParams) => (
  useQuery<SummariesResponse, Error>({
    queryKey: [BILLING_PERFORMANCE_SUMMARIES_QUERY_KEY, params],
    queryFn: fetchSummaries,
    enabled: !!params.startDate && !!params.endDate,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })
);

// Order Details Query
type OrderDetailsQueryKey = [typeof BILLING_PERFORMANCE_ORDER_DETAILS_QUERY_KEY, DetailParams];

const fetchOrderDetails = async ({ queryKey }: QueryFunctionContext<OrderDetailsQueryKey>) => {
  const [, params] = queryKey;
  const searchParams = new URLSearchParams();

  const startDate = formatDate(params.startDate);
  const endDate = formatDate(params.endDate);

  if (startDate) searchParams.set('start_date', startDate);
  if (endDate) searchParams.set('end_date', endDate);
  if (params.groupBy) searchParams.set('group_by', params.groupBy);
  if (params.entityId) searchParams.set('entity_id', String(params.entityId));
  if (params.entityName) searchParams.set('entity_name', params.entityName);
  if (params.locationId) searchParams.set('location_id', String(params.locationId));
  if (params.page) searchParams.set('page', String(params.page));
  if (params.perPage) searchParams.set('per_page', String(params.perPage));
  if (params.sort) searchParams.set('sort', params.sort);
  addPeriodModeParam(searchParams, params.periodMode);
  if (params.summaryDate) searchParams.set('summary_date', params.summaryDate);

  const { data } = await axios.get<OrderDetailsResponse>(
    `/api/v5/billing_performances/order_details?${searchParams.toString()}`
  );
  return data;
};

export const useQueryBillingPerformanceOrderDetails = (params: DetailParams, enabled = true) => (
  useQuery<OrderDetailsResponse, Error>({
    queryKey: [BILLING_PERFORMANCE_ORDER_DETAILS_QUERY_KEY, params],
    queryFn: fetchOrderDetails,
    enabled: enabled && !!params.startDate && !!params.endDate,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })
);

// Route Details Query
type RouteDetailsQueryKey = [typeof BILLING_PERFORMANCE_ROUTE_DETAILS_QUERY_KEY, DetailParams];

const fetchRouteDetails = async ({ queryKey }: QueryFunctionContext<RouteDetailsQueryKey>) => {
  const [, params] = queryKey;
  const searchParams = new URLSearchParams();

  const startDate = formatDate(params.startDate);
  const endDate = formatDate(params.endDate);

  if (startDate) searchParams.set('start_date', startDate);
  if (endDate) searchParams.set('end_date', endDate);
  if (params.groupBy) searchParams.set('group_by', params.groupBy);
  if (params.entityId) searchParams.set('entity_id', String(params.entityId));
  if (params.entityName) searchParams.set('entity_name', params.entityName);
  if (params.locationId) searchParams.set('location_id', String(params.locationId));
  if (params.page) searchParams.set('page', String(params.page));
  if (params.perPage) searchParams.set('per_page', String(params.perPage));
  if (params.sort) searchParams.set('sort', params.sort);
  addPeriodModeParam(searchParams, params.periodMode);
  if (params.summaryDate) searchParams.set('summary_date', params.summaryDate);

  const { data } = await axios.get<RouteDetailsResponse>(
    `/api/v5/billing_performances/route_details?${searchParams.toString()}`
  );
  return data;
};

export const useQueryBillingPerformanceRouteDetails = (params: DetailParams, enabled = true) => (
  useQuery<RouteDetailsResponse, Error>({
    queryKey: [BILLING_PERFORMANCE_ROUTE_DETAILS_QUERY_KEY, params],
    queryFn: fetchRouteDetails,
    enabled: enabled && !!params.startDate && !!params.endDate,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })
);
